import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { App } from './App';
import { notifyMutation } from './api/feedback';
import { SessionProvider } from './session/SessionContext';
import { Toaster } from './ui/Toast';

import './styles/index.css';
import './styles/app.css';
import './styles/doc-sections.css';
import './styles/topbar.css';
import './styles/infotip.css';
import './styles/toolbar.css';
import './styles/account.css';
import './styles/tables.css';
import './styles/users-roles.css';
import './styles/whats-new.css';
import './styles/list-detail.css';
import './styles/process-detail.css';

const queryClient = new QueryClient({
  // Every save confirms itself, or says why it failed, as a toast (api/feedback.ts).
  mutationCache: new MutationCache(notifyMutation),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
      // Never retry an auth failure: 401 ends the session, 403 is a refusal.
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 1;
      },
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root is missing from index.html.');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <BrowserRouter>
          <App />
          <Toaster />
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
